/** @format */
import isPropValid from '@emotion/is-prop-valid';
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent, KeyboardEvent, MouseEvent } from 'react';
import styled from 'styled-components';

import PlusIcon from '@toucan/client/global/icons/plus.svg';

import { shouldKeyDownTriggerEvent } from '@toucan/client/core/events';

import { styles } from './add-button.styles';

export type AddButtonProps = {
    /**
     * If a loading state should be displayed
     */
    isLoading?: boolean;

    /**
     * Handler for when the add button is clicked.
     */
    onAddClick?: (
        event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>
    ) => void;
};

const StyledAddButton = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const AddButton: FunctionComponent<AddButtonProps> = ({
    isLoading,
    onAddClick,
}: AddButtonProps) => {
    const { t } = useTranslation();

    const handleAddClick = (
        event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>
    ) => {
        event.preventDefault();
        event.stopPropagation();
        onAddClick?.(event);
    };

    const handleKeyDownEvent = (event: KeyboardEvent<HTMLElement>) => {
        if (shouldKeyDownTriggerEvent(event)) {
            onAddClick?.(event);
        }
    };

    return (
        <StyledAddButton
            onClick={handleAddClick}
            tabIndex={0}
            onKeyDown={handleKeyDownEvent}
            role="button"
            aria-label={t('Add')}
        >
            <Icon
                variant={IconVariant.ACTIONABLE}
                size={IconSize.L}
                isLoading={isLoading}
            >
                <PlusIcon />
            </Icon>
        </StyledAddButton>
    );
};
